import Header from '@/components/Header/Header';
import PageTitle from '@/components/PageTitle';
import { getInvitationPublic } from '@/core/api/invitations.service';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { PublicRoutesEnum } from '@/core/enums';
import { InvitationWithCompanyName } from '@/core/types';
import TipIcon from '@/icons/tip-green-circle.svg?react';
import { useTranslation } from 'react-i18next';
import RegisterTaxAvisorForm from './RegisterUser/RegisterTaxAvisorForm';
import RegisterUserVerificationForm from './RegisterUser/RegisterUserVerificationForm';
import { Loader } from '@/components/Loader/Loader';
import { DEFAULT_ERROR } from '@/components/Notifications/Notification';
import ErrorIcon from '@/icons/invitation-error.svg?react';

export const RegisterTaxAdvisorWithInvitation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const invitationHash = searchParams.get('invitation');

  const [invitation, setInvitation] = useState<InvitationWithCompanyName>();

  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [isVerification, setIsVerification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (invitationHash) {
      setIsLoading(true);
      getInvitationPublic(invitationHash)
        .then((res) => {
          setInvitation(res);
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err.response?.data?.msg || DEFAULT_ERROR);
          setIsLoading(false);
        });
    } else {
      navigate(PublicRoutesEnum.Page404);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationHash]);

  const onRegisterSubmit = (email: string, password: string) => {
    setEmail(email);
    setPassword(password);
    setIsVerification(true);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      className='flex min-h-screen flex-col
    bg-[linear-gradient(90deg,rgba(255,255,255,1)50%,rgba(244,245,247,1)0%,rgba(244,245,247,1)100%)]'
      data-testid='RegisterTaxAdvisorWithInvitation'
    >
      <PageTitle title='Register Tax Advisor' />
      <Header />
      {!!error && <div className='h-2 w-full bg-red-500' />}
      <div className='mx-auto flex w-full max-w-[1440px] flex-grow items-stretch'>
        <div className='w-full bg-white px-6 py-8 lg:w-1/2 xl:px-14'>
          <div className='RegisterUser__card mx-auto lg:mx-[unset]'>
            {isVerification ? (
              <RegisterUserVerificationForm email={email} password={password} />
            ) : (
              <>
                <div className='text-h4 font-black'>{t('Create account')}</div>
                <div className='mb-4'>
                  {t(
                    "When you set up the account, you gain access to your clients' accounting area."
                  )}
                </div>
                <div className='relative'>
                  <RegisterTaxAvisorForm
                    invitationId={invitation?.id}
                    uuid={invitation?.company_id}
                    onSubmit={onRegisterSubmit}
                  />
                  {!!error && (
                    <div className='absolute left-0 top-0 h-full w-full bg-white/50 backdrop-blur-[1px]' />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className='hidden w-1/2 flex-col items-center bg-[#F4F5F7] pt-32 lg:flex'>
          {!!error ? (
            <div className='mx-auto rounded-lg bg-white px-4 pb-6 pt-4'>
              <ErrorIcon />
              <div className='mt-4 text-h4 font-extrabold'>{t(error)}</div>
              <div className='max-w-[352px] font-semibold'>
                {t('Please reach out to the company that sent the invitation.')}
              </div>
            </div>
          ) : (
            <div className='mx-auto space-y-2 rounded-lg bg-white px-4 pb-6 pt-4'>
              <div className='text-h4 font-extrabold'>
                {t('After creating the account')}
              </div>
              {[
                'Generate and download DATEV exports',
                'Download Proforma invoices',
                "See your client's VAT settings",
                'Get access to Invoice book',
              ].map((v) => (
                <div className='flex items-center gap-x-2' key={v}>
                  <TipIcon className='h-4 w-4 [&>circle]:fill-dark' />
                  <div>{t(v)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
